import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {getSavedSearchesCounts} from 'src/openapi/admin/apis/UserContentCountApi';
import {useAppDispatch} from 'src/store/hooks';

import {makeSelectSearchesByUserId, selectSearchesReceivedAt} from '../slices/searchesSelectors';
import {UserPageState, invalidateUserSearches} from '../slices/searchesSlice';

type Props = {
    scopeKey?: string,
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export const useUserSearchesCount = ({userId, autoload = true, ttlMs = 120_000}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const selectSearches = useMemo(
        () => makeSelectSearchesByUserId(userId),
        [userId],
    );

    const {total, status, error} = useSelector(selectSearches);

    const refetch = useCallback(() => {
        apiDispatch(getSavedSearchesCounts({userId}));
    }, [apiDispatch, userId]);

    const invalidate = useCallback(() => {
        dispatch(invalidateUserSearches(userId));
    }, [dispatch, userId]);

    const receivedAt = useSelector((state: UserPageState) => selectSearchesReceivedAt(state, userId));

    useEffect(() => {
        if (!autoload) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, status, receivedAt, ttlMs, refetch]);

    return {
        total,
        status,
        error,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
};
